import React, { useState, useEffect, useRef, ReactNode } from "react";
import {
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TableSortLabel,
  Box,
  Typography,
  CircularProgress,
  TablePagination,
  Checkbox,
  TextField,
  InputAdornment,
  Collapse,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

// Type definitions
interface TableHeader<T = any> {
  key: string;
  displayName: string;
  minWidth?: number | string;
  format?: (args: { value: any; row: T }) => ReactNode;
  sortValue?: (value: any) => any;
  searchValue?: (args: { value: any; row: T }) => string;
  disableColumnSearch?: boolean;
  disableSelectionIf?: (row: T) => boolean;
}

interface TableActions {
  edit?: (rowId: string | number) => void;
  delete?: (rowId: string | number) => void;
  view?: (rowId: string | number) => void;
  updateShippingAddress?: (rowId: string | number) => void;
}

type TableFilters = Record<string, string | string[] | "all">;

interface TableProps<T extends Record<string, any> = any> {
  headers?: TableHeader<T>[];
  rows?: T[];
  actions?: TableActions;
  filters?: TableFilters;
  loading?: boolean;
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  selectionDisabled?: boolean;
  enableColumnSearch?: boolean;
  expandableRows?: boolean;
  getExpandableContent?: ((row: T) => ReactNode) | null;
  getRowCount?: ((row: T) => number) | null;
  rowKey?: string;
  disableSelectionIf?: ((row: T) => boolean) | null;
  expandedRows?: Set<string | number>;
  onExpandedRowsChange?: (expandedRows: Set<string | number>) => void;
  defaultSortKey?: string;
  defaultSortOrder?: "asc" | "desc";
  defaultRowsPerPage?: number;
  // Server-side pagination/filtering mode: the rows prop is already the
  // filtered, sorted and paginated slice returned by the server. The table
  // renders it as-is and reports control changes (sort, page, rows per page,
  // column search) back to the parent, which re-fetches.
  serverPagination?: boolean;
  totalCount?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (page: number) => void;
  onRowsPerPageChange?: (rowsPerPage: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortChange?: (sortBy: string, sortOrder: "asc" | "desc") => void;
  onColumnSearchChange?: (searchFilters: Record<string, string>) => void;
  // Constrains the table container height so the table scrolls internally
  // with its sticky header, instead of scrolling with the page. Use when a
  // sticky control bar sits above the table (default "100%" = current
  // behavior).
  maxHeight?: string | number;
}

const EMPTY_ARRAY: any[] = [];
const EMPTY_OBJECT: Record<string, any> = {};

const Table = <T extends Record<string, any> = any>({
  headers = EMPTY_ARRAY,
  rows = EMPTY_ARRAY,
  actions = EMPTY_OBJECT,
  filters = EMPTY_OBJECT,
  loading = false,
  onSelectionChange,
  selectionDisabled = false,
  enableColumnSearch = false,
  expandableRows = false,
  getExpandableContent = null,
  getRowCount = null,
  rowKey = "id",
  disableSelectionIf = null,
  expandedRows: externalExpandedRows,
  onExpandedRowsChange,
  defaultSortKey = "",
  defaultSortOrder = "asc",
  defaultRowsPerPage = 10,
  serverPagination = false,
  totalCount: totalCountProp,
  page: pageProp,
  rowsPerPage: rowsPerPageProp,
  onPageChange,
  onRowsPerPageChange,
  sortBy,
  sortOrder,
  onSortChange,
  onColumnSearchChange,
  maxHeight = "100%",
}: TableProps<T>) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [order, setOrder] = useState<"asc" | "desc">(defaultSortOrder);
  const [orderBy, setOrderBy] = useState<string>(defaultSortKey);
  const [filteredRows, setFilteredRows] = useState<T[]>(rows);

  // Mirrors `filteredRows` so the filter effect below can detect whether a
  // freshly computed array actually differs before committing it. `filter()`
  // always returns a new array reference, so committing unconditionally —
  // combined with callers that pass inline `headers`/`rows` (a new identity
  // every render) — triggers an endless update loop ("Maximum update depth
  // exceeded").
  const filteredRowsRef = useRef<T[]>(rows);

  // Selection state
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const selectedRowsRef = useRef<(string | number)[]>(selectedRows);

  // Search state for column filters
  const [searchFilters, setSearchFilters] = useState<Record<string, string>>({});

  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(defaultRowsPerPage);

  // Expandable rows state
  const [internalExpandedRows, setInternalExpandedRows] = useState<Set<string | number>>(new Set());
  const expandedRows: Set<string | number> =
    externalExpandedRows !== undefined
      ? externalExpandedRows
      : internalExpandedRows;
  const setExpandedRows = onExpandedRowsChange || setInternalExpandedRows;

  // Update ref when selectedRows changes
  useEffect(() => {
    selectedRowsRef.current = selectedRows;
  }, [selectedRows]);

  useEffect(() => {
    if (serverPagination) {
      // The server already filtered/sorted/paginated the rows. Keep the
      // local copy in sync and prune selection to rows actually visible on
      // the current page (selection cannot span pages in server mode).
      if (filteredRowsRef.current !== rows) {
        filteredRowsRef.current = rows;
        setFilteredRows(rows);
      }
      const currentRowIds = new Set(rows.map((row) => row[rowKey]));
      const validSelectedRows = selectedRowsRef.current.filter((id) =>
        currentRowIds.has(id),
      );
      if (
        validSelectedRows.length !== selectedRowsRef.current.length ||
        !validSelectedRows.every((id) => selectedRowsRef.current.includes(id))
      ) {
        setSelectedRows(validSelectedRows);
        onSelectionChange?.(validSelectedRows);
      }
      return;
    }

    const newFilteredRows =
      rows?.filter((row) => {
        // Apply existing filters (like role, status filters)
        for (const key in filters) {
          if (Object.prototype.hasOwnProperty.call(filters, key)) {
            const filterValues = filters[key];

            // Skip filter if value is "all" (show all items)
            if (filterValues === "all") {
              continue;
            }

            // Handle array filters (multiple selections)
            if (Array.isArray(filterValues) && filterValues.length > 0) {
              if (!filterValues.includes(row[key])) {
                return false;
              }
            }
            // Handle string filters (single selection)
            else if (typeof filterValues === "string" && filterValues !== "") {
              if (row[key] !== filterValues) {
                return false;
              }
            }
          }
        }

        // Apply search filters (text search per column) - only if column search is enabled
        if (enableColumnSearch) {
          for (const key in searchFilters) {
            if (Object.prototype.hasOwnProperty.call(searchFilters, key)) {
              const searchTerm = searchFilters[key]?.toLowerCase();
              if (searchTerm && searchTerm.trim() !== "") {
                let shouldInclude = false;

                // Special handling for customerNames column - search through line items
                if (key === "customerNames") {
                  const lineItems = (row as any).lineItems as Array<{ customerName?: string }> | undefined;
                  shouldInclude =
                    lineItems?.some((lineItem) =>
                      lineItem.customerName?.toLowerCase().includes(searchTerm),
                    ) || false;
                } else {
                  const header = headers.find((h) => h.key === key);
                  // Get the raw cell value
                  const rawCellValue = String(row[key] || "").toLowerCase();
                  let formattedCellValue = rawCellValue;
                  let customSearchValue = "";

                  if (header?.searchValue) {
                    try {
                      customSearchValue = header.searchValue({
                        value: row[key],
                        row,
                      }).toLowerCase();
                    } catch {
                      customSearchValue = "";
                    }
                  }

                  if (header && header.format) {
                    try {
                      // Get the formatted value for search
                      const formattedResult = header.format({
                        value: row[key],
                        row,
                      });
                      // If it's a React element, we can't search on it
                      if (typeof formattedResult === "string") {
                        formattedCellValue = formattedResult.toLowerCase();
                      }
                    } catch {
                      // If formatting fails, fall back to raw value
                      formattedCellValue = rawCellValue;
                    }
                  }

                  // Check if search term is in either raw or formatted value
                  shouldInclude =
                    rawCellValue.includes(searchTerm) ||
                    formattedCellValue.includes(searchTerm) ||
                    customSearchValue.includes(searchTerm);
                }

                if (!shouldInclude) {
                  return false;
                }
              }
            }
          }
        }

        return true;
      }) || [];
    // `filter` always creates a new array reference. Only commit when the row
    // content actually changed (same object references, or equal defined
    // rowKeys) — this keeps the effect idempotent even when props such as
    // inline headers change identity on every render.
    const prevRows = filteredRowsRef.current;
    const sameContent =
      prevRows.length === newFilteredRows.length &&
      prevRows.every((row, i) => {
        const nextRow = newFilteredRows[i];
        if (row === nextRow) return true;
        const prevKey = row[rowKey];
        const nextKey = nextRow[rowKey];
        return (
          prevKey !== undefined &&
          nextKey !== undefined &&
          prevKey === nextKey
        );
      });
    if (!sameContent) {
      filteredRowsRef.current = newFilteredRows;
      setFilteredRows(newFilteredRows);
    }
    // Keep selection for rows that are still visible
    const currentRowIds = new Set(newFilteredRows.map((row) => row[rowKey]));
    const validSelectedRows = selectedRowsRef.current.filter((id) =>
      currentRowIds.has(id),
    );
    if (
      validSelectedRows.length !== selectedRowsRef.current.length ||
      !validSelectedRows.every((id) => selectedRowsRef.current.includes(id))
    ) {
      setSelectedRows(validSelectedRows);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- headers is used
    // only for searchValue/format lookups inside the filter, but must not be a
    // dependency: callers pass inline header arrays (a new identity every
    // render), which re-ran this effect endlessly and crashed the table.
  }, [rows, filters, searchFilters, enableColumnSearch, rowKey, serverPagination, onSelectionChange]);

  const effectiveOrderBy = serverPagination ? (sortBy ?? defaultSortKey) : orderBy;
  const effectiveOrder = serverPagination ? (sortOrder ?? defaultSortOrder) : order;

  const handleSort = (property: string) => () => {
    if (serverPagination) {
      const isAsc = effectiveOrderBy === property && effectiveOrder === "asc";
      onSortChange?.(property, isAsc ? "desc" : "asc");
      return;
    }
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSearchChange = (columnKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    const next = {
      ...searchFilters,
      [columnKey]: value,
    };
    setSearchFilters(next);
    if (serverPagination) {
      // The parent re-fetches (debounced) with the new per-column search.
      onColumnSearchChange?.(next);
      return;
    }
    setPage(0); // Reset to first page when searching
  };

  const handleClearSearch = (columnKey: string) => () => {
    const next = {
      ...searchFilters,
      [columnKey]: "",
    };
    setSearchFilters(next);
    if (serverPagination) {
      onColumnSearchChange?.(next);
      return;
    }
    setPage(0);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    if (serverPagination) {
      onPageChange?.(newPage);
      return;
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);
    if (serverPagination) {
      onRowsPerPageChange?.(value);
      return;
    }
    setRowsPerPage(value);
    setPage(0); // Reset to the first page
  };

  // Helper function to check if selection is disabled for a row
  const isSelectionDisabled = (row: T): boolean => {
    // Check global disableSelectionIf first
    if (disableSelectionIf && typeof disableSelectionIf === "function") {
      return disableSelectionIf(row);
    }

    // Check if any header has a disableSelectionIf condition that returns true
    return headers.some((header) => {
      if (
        header.disableSelectionIf &&
        typeof header.disableSelectionIf === "function"
      ) {
        return header.disableSelectionIf(row);
      }
      return false;
    });
  };

  const handleRowSelect = (rowId: string | number) => {
    const newSelectedRows = selectedRows.includes(rowId)
      ? selectedRows.filter((id) => id !== rowId)
      : [...selectedRows, rowId];

    setSelectedRows(newSelectedRows);
    if (onSelectionChange) {
      onSelectionChange(newSelectedRows);
    }
  };

  const handleRowExpand = (rowId: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
    } else {
      newExpandedRows.add(rowId);
    }
    setExpandedRows(newExpandedRows);
  };

  const handleSelectAll = () => {
    // Get all displayed rows that can be selected (not disabled)
    const selectableRows = displayRows.filter(
      (row) => !isSelectionDisabled(row),
    );
    const selectableRowIds = selectableRows.map((row) => row[rowKey]);

    const newSelectedRows =
      selectedRows.length === selectableRowIds.length ? [] : selectableRowIds;

    setSelectedRows(newSelectedRows);
    if (onSelectionChange) {
      onSelectionChange(newSelectedRows);
    }
  };

  const handleExpandAll = () => {
    const allRowIds = new Set(filteredRows.map((row) => row[rowKey]));
    const newExpandedRows: Set<string | number> =
      expandedRows.size === allRowIds.size ? new Set() : allRowIds;
    setExpandedRows(newExpandedRows);
  };

  const hasActions = !!(
    actions.edit ||
    actions.delete ||
    actions.view ||
    actions.updateShippingAddress
  );
  const hasSelection = !!onSelectionChange;

  const stableSort = <T,>(array: T[], comparator: (a: T, b: T) => number): T[] => {
    const stabilizedThis = array?.map((el, index) => [el, index] as [T, number]);
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  };

  function descendingComparator<T>(a: T, b: T, orderBy: string): number {
    const header = headers.find((h) => h.key === orderBy);
    const aRaw = (a as any)[orderBy];
    const bRaw = (b as any)[orderBy];
    const aVal = header?.sortValue ? header.sortValue(aRaw) : aRaw;
    const bVal = header?.sortValue ? header.sortValue(bRaw) : bRaw;
    // Push null/undefined to the end regardless of sort direction
    if (aVal == null && bVal == null) return 0;
    if (aVal == null) return 1;
    if (bVal == null) return -1;
    if (bVal < aVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }

  function getComparator<T,>(order: "asc" | "desc", orderBy: string): (a: T, b: T) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  const sortedRows = stableSort(filteredRows, getComparator(effectiveOrder, effectiveOrderBy));

  // In server mode the rows prop is already the requested page; pagination
  // and sorting state are controlled by the parent.
  const totalCount = serverPagination
    ? (totalCountProp ?? filteredRows.length)
    : filteredRows.length;
  const effectiveRowsPerPage = serverPagination ? (rowsPerPageProp ?? 10) : rowsPerPage;
  const effectivePage = serverPagination
    ? Math.min(
        pageProp ?? 0,
        Math.max(0, Math.ceil(totalCount / Math.max(1, effectiveRowsPerPage)) - 1),
      )
    : page;
  const displayRows = serverPagination
    ? filteredRows
    : sortedRows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <TableContainer component={Paper} sx={{ maxHeight }}>
        <MuiTable>
          <TableHead
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              backgroundColor: "#555759",
            }}
          >
            <TableRow>
              {expandableRows && (
                <TableCell sx={{ fontWeight: "bold", width: "50px" }}>
                  <IconButton
                    size="small"
                    onClick={handleExpandAll}
                    disabled={loading}
                    sx={{ p: 0.5 }}
                  >
                    {expandedRows.size === filteredRows.length ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </TableCell>
              )}
              {hasSelection && (
                <TableCell sx={{ fontWeight: "bold", width: "50px" }}>
                  <Checkbox
                    sx={{ color: "#fff", "&.Mui-checked": { color: "#fff" } }}
                    indeterminate={
                      selectedRows.length > 0 &&
                      selectedRows.length <
                        displayRows.filter((row) => !isSelectionDisabled(row))
                          .length
                    }
                    checked={
                      displayRows.filter((row) => !isSelectionDisabled(row))
                        .length > 0 &&
                      selectedRows.length ===
                        displayRows.filter((row) => !isSelectionDisabled(row))
                          .length
                    }
                    onChange={handleSelectAll}
                    disabled={loading || selectionDisabled}
                  />
                </TableCell>
              )}
              {hasActions && (
                <TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
              )}
              {headers.map((header, index) => (
                <TableCell
                  key={index}
                  sx={{
                    fontWeight: "bold",
                    minWidth: header.minWidth || "auto",
                  }}
                  sortDirection={effectiveOrderBy === header.key ? effectiveOrder : false}
                >
                  <TableSortLabel
                    active={effectiveOrderBy === header.key}
                    direction={effectiveOrderBy === header.key ? effectiveOrder : "asc"}
                    onClick={handleSort(header.key)}
                    disabled={loading}
                  >
                    {header.displayName}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
            {/* Search row - only show if column search is enabled */}
            {enableColumnSearch && (
              <TableRow sx={{ backgroundColor: "var(--tpc-50, #f7f7f7)" }}>
                {expandableRows && <TableCell sx={{ padding: "8px" }} />}
                {hasSelection && <TableCell sx={{ padding: "8px" }} />}
                {hasActions && <TableCell sx={{ padding: "8px" }} />}
                {headers.map((header, index) => (
                  <TableCell key={`search-${index}`} sx={{ padding: "8px" }}>
                    {header.disableColumnSearch ? (
                      <Box sx={{ minHeight: "40px" }} />
                    ) : (
                      <TextField
                        size="small"
                        placeholder="Search"
                        value={searchFilters[header.key] || ""}
                        onChange={handleSearchChange(header.key)}
                        disabled={loading}
                        sx={{
                          minWidth: header.minWidth || 120,
                          "& .MuiInputBase-root": {
                            fontSize: "0.875rem",
                            pr: "4.5px",
                          },
                          "& .MuiInputBase-input": {
                            padding: "4.5px 14px",
                          },
                        }}
                        InputProps={{
                          endAdornment: searchFilters[header.key] ? (
                            <InputAdornment position="end">
                              <IconButton
                                size="small"
                                onClick={handleClearSearch(header.key)}
                                sx={{ padding: 0.5 }}
                                disabled={loading}
                              >
                                <ClearIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </InputAdornment>
                          ) : null,
                          sx: { borderRadius: 0 },
                        }}
                      />
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableHead>
          <TableBody sx={{ position: "relative" }}>
            {loading && (
              <TableRow
                sx={{
                  position: "absolute",
                  minHeight: 250,
                  height: "100%",
                  width: "100%",
                }}
              >
                <TableCell
                  colSpan={
                    headers.length +
                    (hasActions ? 1 : 0) +
                    (hasSelection ? 1 : 0) +
                    (expandableRows ? 1 : 0)
                  }
                  sx={{
                    padding: 0,
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 1,
                    borderBottom: "none",
                    backgroundColor: isDarkMode
                      ? "rgba(44, 62, 80, 0.7)"
                      : "rgba(255, 255, 255, 0.7)",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                      width: "100%",
                      height: "100%",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    <Typography variant="body2">Loading...</Typography>
                    <CircularProgress size={24} color="inherit" />
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {displayRows?.map((row) => (
              <React.Fragment key={row[rowKey]}>
                <TableRow
                  sx={{
                    "& .MuiTableCell-root": {
                      p: 1,
                    },
                    "&:last-child td, &:last-child th": {
                      border: 0,
                    },
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.03)",
                    },
                    ...(expandableRows &&
                      expandedRows.has(row[rowKey]) && {
                        backgroundColor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                      }),
                  }}
                >
                  {expandableRows && (
                    <TableCell sx={{ borderBottom: "none" }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleRowExpand(row[rowKey])}
                        >
                          {expandedRows.has(row[rowKey]) ? (
                            <KeyboardArrowDownIcon />
                          ) : (
                            <KeyboardArrowRightIcon />
                          )}
                        </IconButton>
                        {getRowCount && (
                          <Chip
                            label={getRowCount(row)}
                            size="small"
                            variant="filled"
                            sx={{
                              height: "18px",
                              width: "18px",
                              fontSize: "0.7rem",
                              minWidth: "auto",
                              "& .MuiChip-label": {
                                px: 0.5,
                              },
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {hasSelection && (
                    <TableCell
                      sx={expandableRows ? { borderBottom: "none" } : {}}
                    >
                      <Checkbox
                        sx={{ ml: 1 }}
                        checked={selectedRows.includes(row[rowKey])}
                        onChange={() => handleRowSelect(row[rowKey])}
                        disabled={
                          loading ||
                          selectionDisabled ||
                          isSelectionDisabled(row)
                        }
                      />
                    </TableCell>
                  )}
                  {hasActions && (
                    <TableCell
                      sx={expandableRows ? { borderBottom: "none" } : {}}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          justifyContent: "center",
                          flexWrap: "no-wrap",
                        }}
                      >
                        {actions.view && (
                          <IconButton
                            size="small"
                            aria-label="view"
                            onClick={() => actions.view?.(row[rowKey])}
                            disabled={row?.status === "deleted" || loading}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        )}
                        {actions.edit && (
                          <IconButton
                            size="small"
                            aria-label="edit"
                            onClick={() => actions.edit?.(row[rowKey])}
                            disabled={row?.status === "deleted" || loading}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                        {actions.delete && row?.status !== "deleted" && (
                          <IconButton
                            size="small"
                            aria-label="delete"
                            onClick={() => actions.delete?.(row[rowKey])}
                            disabled={loading}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        )}
                        {actions.updateShippingAddress && (
                          <IconButton
                            size="small"
                            aria-label="update shipping address"
                            onClick={() =>
                              actions.updateShippingAddress?.(row[rowKey])
                            }
                            disabled={loading}
                          >
                            <LocationOnIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  )}
                  {headers?.map((header, cellIndex) => (
                    <TableCell
                      key={cellIndex}
                      sx={{
                        minWidth: header.minWidth || "auto",
                        ...(expandableRows && { borderBottom: "none" }),
                      }}
                    >
                      {header.format
                        ? header.format({ value: row[header.key], row })
                        : row[header.key]}
                    </TableCell>
                  ))}
                </TableRow>
                {expandableRows && (
                  <TableRow
                    sx={{
                      ...(expandedRows.has(row[rowKey]) && {
                        backgroundColor: isDarkMode
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 0, 0, 0.02)",
                      }),
                    }}
                  >
                    <TableCell
                      style={{ paddingBottom: 0, paddingTop: 0 }}
                      colSpan={
                        (expandableRows ? 1 : 0) +
                        (hasActions ? 1 : 0) +
                        (hasSelection ? 1 : 0) +
                        headers.length
                      }
                    >
                      <Collapse
                        in={expandedRows.has(row[rowKey])}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ margin: 1 }}>
                          {getExpandableContent && getExpandableContent(row)}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
            {(serverPagination ? totalCount : filteredRows?.length) === 0 && (
              <TableRow>
                <TableCell
                  colSpan={
                    headers.length +
                    (hasActions ? 1 : 0) +
                    (hasSelection ? 1 : 0) +
                    (expandableRows ? 1 : 0)
                  }
                  sx={{ borderBottom: "none", p: 0 }}
                >
                  <div className="ui-table__empty">
                    <div className="ui-strong">No data available</div>
                    <div className="ui-caption ui-mt-8">
                      Rows appear here once the current filters find something.
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={
          serverPagination
            ? [5, 10, 25, 50]
            : [5, 10, 25, 50, { label: "All", value: -1 }]
        }
        component="div"
        count={totalCount}
        rowsPerPage={effectiveRowsPerPage}
        page={effectivePage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        disabled={loading}
        slotProps={{
          select: {
            MenuProps: {
              sx: {
                zIndex: 10000,
              },
            },
          },
        }}
      />
    </Box>
  );
};

export default Table;
